/**
 * List / Map segmented control for directory screens (Shops map mode).
 * Visual recipe matches the former inline control on ShopsDirectoryScreen.
 */

export interface DirectoryListMapViewSwitchProps {
  readonly viewMode: 'list' | 'map';
  readonly onViewModeChange: (mode: 'list' | 'map') => void;
  readonly listLabel: string;
  readonly mapLabel: string;
  readonly groupAriaLabel: string;
  readonly listAriaLabel: string;
  readonly mapAriaLabel: string;
  /** e.g. customer-shops-directory → `${prefix}-view-mode`, `-view-list`, `-view-map` */
  readonly testIdPrefix: string;
  readonly className?: string;
}

const SEGMENT_BASE = [
  'inline-flex min-h-11 min-w-11 flex-1 items-center justify-center rounded-md px-3 text-sm font-medium',
  'whitespace-nowrap',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

function segmentClass(active: boolean): string {
  return [
    SEGMENT_BASE,
    active
      ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-sm'
      : 'bg-[var(--color-surface-elevated)] text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
  ].join(' ');
}

export function DirectoryListMapViewSwitch({
  viewMode,
  onViewModeChange,
  listLabel,
  mapLabel,
  groupAriaLabel,
  listAriaLabel,
  mapAriaLabel,
  testIdPrefix,
  className,
}: DirectoryListMapViewSwitchProps): JSX.Element {
  return (
    <div
      className={[
        'flex min-w-0 w-full max-w-sm self-start gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1 sm:w-auto sm:min-w-[14rem]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-label={groupAriaLabel}
      data-testid={`${testIdPrefix}-view-mode`}
    >
      <button
        type="button"
        className={segmentClass(viewMode === 'list')}
        aria-pressed={viewMode === 'list'}
        aria-label={listAriaLabel}
        data-testid={`${testIdPrefix}-view-list`}
        onClick={() => {
          onViewModeChange('list');
        }}
      >
        {listLabel}
      </button>
      <button
        type="button"
        className={segmentClass(viewMode === 'map')}
        aria-pressed={viewMode === 'map'}
        aria-label={mapAriaLabel}
        data-testid={`${testIdPrefix}-view-map`}
        onClick={() => {
          onViewModeChange('map');
        }}
      >
        {mapLabel}
      </button>
    </div>
  );
}
